import { FeatureName, useIsFeatureEnabled } from '@gripp/shared-logic';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { SearchInput } from '../crop-circle/Components/SearchInput';
import { colors as CropCircleColors } from '../crop-circle/tokens';
import { DebouncedTextInput } from '../forms';

type EntitySearchInputProps = {
  filter: { keyword?: string } | undefined | null;
  onSearch: (keyword: string) => Promise<void>;
  placeholder: string;
  onBlur?: () => void;
  /**
   * Pass autoFocus={!isLoading && !filter?.keyword} to opt in to auto-focusing
   * when there is no pre-existing keyword (e.g. Assets).
   */
  autoFocus?: boolean;
};

export const EntitySearchInput = ({
  filter,
  onSearch,
  placeholder,
  onBlur,
  autoFocus = false,
}: EntitySearchInputProps) => {
  const isCropCircleRedesign = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  const [text, setText] = useState<string>(filter?.keyword ?? '');
  // Gate onSearch firing until the user actually types. Prevents the
  // inner SearchInput's mount effect (which fires onDebounce('') when
  // value is initially empty) from clobbering a persisted keyword
  // during navigation between pages that share this filter.
  const hasTyped = useRef(false);

  useEffect(() => {
    if (!text && filter?.keyword) {
      setText(filter.keyword);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter?.keyword]);

  const handleSearch = useCallback(
    async (keyword: string) => {
      if (!hasTyped.current) return;
      await onSearch(keyword);
    },
    [onSearch]
  );

  if (!filter) return null;

  if (isCropCircleRedesign) {
    return (
      <SearchInput
        value={text}
        onChangeText={(value) => {
          if (!hasTyped.current) hasTyped.current = true;
          setText(value);
        }}
        onDebounce={handleSearch}
        placeholder={placeholder}
        autoFocus={autoFocus}
        width={'100%'}
        hideSearchIcon
        hideClearButton
        onBlur={() => {
          if (!text && onBlur) onBlur();
        }}
      />
    );
  }

  return (
    <DebouncedTextInput
      debounceTimeMs={500}
      debounceFunc={handleSearch}
      placeholder={placeholder}
      value={text}
      onBlur={() => {
        if (!text && onBlur) onBlur();
      }}
      onChangeText={(value) => {
        if (!hasTyped.current) hasTyped.current = true;
        setText(value);
      }}
      height={44}
      width={'100%'}
      outlineStyle={{
        ...styles.inputStyle,
        backgroundColor:
          Platform.OS !== 'web' ? CropCircleColors.neutral[200] : 'white',
      }}
      disableInnerStyle={true}
      autoFocus={autoFocus}
    />
  );
};

const styles = StyleSheet.create({
  inputStyle: {
    backgroundColor: CropCircleColors.neutral[200],
    borderWidth: 0,
    borderRadius: 8,
  },
});
